import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  type Database,
  dbCan,
  identityStyle,
  useDeleteDatabase,
  useMoveDatabase,
  useUpdateDatabase,
} from '@/entities/database';
import { lookProps, lookSurface } from '@/entities/appearance';
import { databaseTileData, resourceOf } from '@/entities/section';
import { useWorkspaces } from '@/entities/workspace';
import { useErrorMessage } from '@/shared/i18n';
import { originStateFrom } from '@/shared/lib/origin';
import {
  Button,
  CardActions,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Icons,
  KindIcon,
  MenuItem,
  MenuList,
  MenuRow,
  MetaItem,
  MoveMenuItems,
  Popover,
  ResourceMeta,
  TextField,
  useToast,
} from '@/shared/ui';

import { DATABASE_NAME_MAX } from './CreateDatabaseDialog';
import { DatabaseAppearanceDialog } from './DatabaseAppearanceDialog';
import { DatabaseLabelsDialog } from './DatabaseLabelsDialog';
import { DatabaseFavoriteButton } from './DatabaseFavoriteButton';
import { duplicatedMessage } from './duplicate-message';
import { DuplicateDatabaseDialog } from './DuplicateDatabaseDialog';
import styles from './DatabaseCatalog.module.css';

interface Props {
  databases: readonly Database[];
  /** Workspace names by id: shown on each card (the cross-workspace home catalog). */
  workspaceNames?: ReadonlyMap<string, string>;
  /** Moving up / down is offered in a single workspace's ordered list only. */
  orderable?: boolean;
  emptyText?: string;
}

function RenameDialog({ database, onClose }: { database: Database; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateDatabase(database.id);
  const message = useErrorMessage();
  const [name, setName] = useState(database.name);
  return (
    <Dialog title={t('databases.rename')} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;
          update.mutate({ name: name.trim() }, { onSuccess: onClose });
        }}
      >
        <TextField
          label={t('databases.name')}
          value={name}
          maxLength={DATABASE_NAME_MAX}
          autoFocus
          error={update.error ? message(update.error) : undefined}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <div className={styles.formActions}>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CardMenu({
  database,
  previous,
  next,
  orderable,
  onMoveToSection,
}: {
  database: Database;
  previous: Database | undefined;
  next: Database | undefined;
  orderable: boolean;
  onMoveToSection: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const move = useMoveDatabase(database.workspace_id);
  const remove = useDeleteDatabase();
  const toast = useToast();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const [renaming, setRenaming] = useState(false);
  const [styling, setStyling] = useState(false);
  const [labelling, setLabelling] = useState(false);
  const workspaces = useWorkspaces();
  const inTeam =
    workspaces.data?.find((w) => w.id === database.workspace_id)?.workspace_type === 'team';
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const mayEdit = dbCan(database, 'database.edit_details');
  const mayDelete = dbCan(database, 'database.delete');
  const mayCopy = database.content_visible;
  if (!mayEdit && !mayDelete && !mayCopy) return null;
  return (
    <>
      <Popover
        label={t('databases.menu', { name: database.name })}
        width={240}
        alignEnd
        trigger={(props) => (
          <button
            type="button"
            {...props}
            className={styles.cardMenu}
            aria-label={t('databases.menu', { name: database.name })}
          >
            <Icons.more />
          </button>
        )}
      >
        {(close) => (
          <MenuList>
            {mayEdit && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.edit />}
                  onClick={() => {
                    close();
                    setRenaming(true);
                  }}
                >
                  {t('databases.rename')}
                </MenuItem>
              </MenuRow>
            )}
            {mayEdit && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.palette />}
                  onClick={() => {
                    close();
                    setStyling(true);
                  }}
                >
                  {t('catalog.databaseAppearance')}
                </MenuItem>
              </MenuRow>
            )}
            {mayEdit && inTeam && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.tag />}
                  onClick={() => {
                    close();
                    setLabelling(true);
                  }}
                >
                  {t('databases.labels.action')}
                </MenuItem>
              </MenuRow>
            )}
            {mayEdit && orderable && (
              <MoveMenuItems
                upLabel={t('catalog.moveUp', { name: database.name })}
                downLabel={t('catalog.moveDown', { name: database.name })}
                canMoveUp={previous !== undefined}
                canMoveDown={next !== undefined}
                disabled={move.isPending}
                onUp={() => {
                  close();
                  if (previous) move.mutate({ id: database.id, before_id: previous.id });
                }}
                onDown={() => {
                  close();
                  if (next) move.mutate({ id: database.id, after_id: next.id });
                }}
              />
            )}
            {mayEdit && onMoveToSection && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.folder />}
                  onClick={() => {
                    close();
                    onMoveToSection();
                  }}
                >
                  {t('sections.moveTo')}
                </MenuItem>
              </MenuRow>
            )}
            {mayCopy && (
              <MenuRow>
                <MenuItem
                  icon={<Icons.copy />}
                  onClick={() => {
                    close();
                    setDuplicating(true);
                  }}
                >
                  {t('databases.duplicate.action')}
                </MenuItem>
              </MenuRow>
            )}
            {mayDelete && (
              <MenuRow separated>
                <MenuItem
                  icon={<Icons.trash />}
                  danger
                  onClick={() => {
                    close();
                    setDeleting(true);
                  }}
                >
                  {t('databases.delete')}
                </MenuItem>
              </MenuRow>
            )}
          </MenuList>
        )}
      </Popover>
      {renaming && (
        <RenameDialog
          database={database}
          onClose={() => {
            setRenaming(false);
          }}
        />
      )}
      {styling && (
        <DatabaseAppearanceDialog
          database={database}
          onClose={() => {
            setStyling(false);
          }}
        />
      )}
      {labelling && (
        <DatabaseLabelsDialog
          database={database}
          onClose={() => {
            setLabelling(false);
          }}
        />
      )}
      {duplicating && (
        <DuplicateDatabaseDialog
          source={database}
          onClose={() => {
            setDuplicating(false);
          }}
          onDuplicated={(copy, report) => {
            setDuplicating(false);
            toast.show({ message: duplicatedMessage(t, copy.name, report) });
            void navigate(`/app/databases/${copy.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={t('databases.deleteTitle')}
          message={t('databases.deleteMessage', { name: database.name })}
          confirmLabel={t('databases.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setDeleting(false);
          }}
          onConfirm={() => {
            remove.mutate(database, {
              onSuccess: () => {
                setDeleting(false);
              },
              onError: (error) => {
                setDeleting(false);
                toast.show({ message: message(error) });
              },
            });
          }}
        />
      )}
    </>
  );
}

/** One database card; its leading icon (layers) tells it from a board. */
export function DatabaseCard({
  database,
  workspaceName,
  previous,
  next,
  orderable = false,
  location,
  onMoveToSection,
  onDropBeside,
}: {
  database: Database;
  workspaceName?: string | undefined;
  previous?: Database | undefined;
  next?: Database | undefined;
  /** Moving up / down is offered (manual order of one workspace, no filter). */
  orderable?: boolean;
  /** Where it is filed ("Development / Backend"), shown in search results (UD-STRUCT). */
  location?: string | undefined;
  /** Opens the "Move to section" picker (offered to whoever may edit the database). */
  onMoveToSection?: (() => void) | undefined;
  /** Drag and drop: another database was dropped beside this card (manual order, no filter). */
  onDropBeside?:
    ((sourceId: string, anchor: { after_id: string } | { before_id: string }) => void) | undefined;
}) {
  const { t } = useTranslation();
  const here = useLocation();
  const look = lookProps(database.appearance?.tile);
  const ref = useRef<HTMLLIElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);
  const previousId = previous?.id;
  const nextId = next?.id;
  const dragEnabled = onDropBeside !== undefined && dbCan(database, 'database.edit_details');

  useEffect(() => {
    const element = ref.current;
    if (!element || !dragEnabled) return;
    return combine(
      draggable({
        element,
        getInitialData: () => databaseTileData(database.workspace_id, database.id),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          const dragged = resourceOf(source.data);
          return (
            dragged?.kind === 'database' &&
            dragged.workspaceId === database.workspace_id &&
            dragged.id !== database.id
          );
        },
        getData: ({ input }) =>
          attachClosestEdge(databaseTileData(database.workspace_id, database.id), {
            element,
            input,
            allowedEdges: ['left', 'right'],
          }),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: ({ self, source }) => {
          setEdge(null);
          const dragged = resourceOf(source.data);
          if (dragged?.kind !== 'database') return;
          const side = extractClosestEdge(self.data);
          // Dropped where it already is: nothing to save.
          if (side === 'left' && previousId !== dragged.id) {
            onDropBeside(dragged.id, { before_id: database.id });
          }
          if (side === 'right' && nextId !== dragged.id) {
            onDropBeside(dragged.id, { after_id: database.id });
          }
        },
      }),
    );
  }, [database.id, database.workspace_id, dragEnabled, onDropBeside, previousId, nextId]);

  return (
    <li
      ref={ref}
      className={[
        styles.card,
        lookSurface,
        dragEnabled && styles.cardDraggable,
        dragging && styles.cardDragging,
        edge && styles[`cardDrop-${edge}`],
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="database-card"
      data-color={database.color ?? undefined}
      {...look}
      style={{ ...look.style, ...identityStyle(database.color, '--database-color') }}
    >
      <span className={styles.cardColor} aria-hidden />
      <Link
        to={`/app/databases/${database.id}`}
        state={originStateFrom(here)}
        className={styles.cardLink}
        // A link drags as a link; the whole card is the handle instead.
        draggable={dragEnabled ? false : undefined}
      >
        <span className={styles.cardName}>
          <KindIcon icon={<Icons.layers />} label={t('workspace.objectTypes.database')} />
          <span className={styles.cardTitle} title={database.name}>
            {database.name}
          </span>
          {database.is_confidential && (
            <span className={styles.lock} title={t('databases.confidential')}>
              <Icons.lock />
              <span className="visually-hidden">{t('databases.confidential')}</span>
            </span>
          )}
        </span>
        {location && (
          <span className={styles.cardLocation} data-testid="resource-location">
            <Icons.folder /> {location}
          </span>
        )}
        {database.description && (
          <span className={styles.cardDescription}>{database.description}</span>
        )}
        <ResourceMeta>
          {workspaceName && <MetaItem icon={<Icons.workspace />}>{workspaceName}</MetaItem>}
          <MetaItem icon={<Icons.crown />}>
            {t('databases.ownerName', { name: database.owner_name })}
          </MetaItem>
          {!database.content_visible && <span>{t('databases.accessOnly')}</span>}
        </ResourceMeta>
      </Link>
      {database.labels.length > 0 && (
        <ul className={styles.labelChips} aria-label={t('databases.labels.field')}>
          {database.labels.map((label) => (
            <li key={label.id} className={styles.labelChip} title={label.name}>
              <span aria-hidden className={styles.labelDot} style={{ background: label.color }} />
              <span className={styles.labelName}>{label.name}</span>
            </li>
          ))}
        </ul>
      )}
      <CardActions>
        <DatabaseFavoriteButton database={database} />
        <CardMenu
          database={database}
          previous={previous}
          next={next}
          orderable={orderable}
          onMoveToSection={onMoveToSection}
        />
      </CardActions>
    </li>
  );
}

/** Database cards: name, confidential lock, owner and (in the home catalog) the workspace. */
export function DatabaseList({ databases, workspaceNames, orderable = false, emptyText }: Props) {
  const { t } = useTranslation();
  if (databases.length === 0) return <EmptyState title={emptyText ?? t('databases.empty')} />;
  return (
    <ul className={styles.grid} data-testid="database-list">
      {databases.map((database, index) => (
        <DatabaseCard
          key={database.id}
          database={database}
          workspaceName={workspaceNames?.get(database.workspace_id)}
          orderable={orderable}
          previous={orderable ? databases[index - 1] : undefined}
          next={orderable ? databases[index + 1] : undefined}
        />
      ))}
    </ul>
  );
}
